import React, { useState, useEffect, useMemo, useContext } from "react";
import { gql, useMutation, useQuery } from "urql";
import { QRCodeCanvas } from "qrcode.react";

import { isUnregisteredPaymentRecipientStatus, scaStatusExplanation } from "../../../../../common/src/openPaymentsScaStatus";
import { twoDecPriceFormat } from "../../../../../common/src/utils/FormatFunctions";
import { Panel } from "../../../../../components/src/common/Panel";
import { IPaymentDataRow } from "./OpenPaymentsPaymentsGrid";
import { OP_SIGNING_BASKET_AUTHORIZATION_QUERY, OP_PAYMENTS_QUERY } from "../../../../../graphql/src/oppayment/queries";
import { OpPayment, OpPaymentStatusEnum, ScaStatus } from "../../../types.generated";
import { OPItemContext } from "../../../contexts/OPItemContext";

interface PaymentSignPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    paymentIds: string[];
    refetch: (input: string[], type: "refetch" | "delete") => Promise<void>;
}

const OP_CREATE_SIGNING_BASKET = gql`
    mutation opCreateSigningBasket($idIn: [GraphQLObjectId!]!) {
        opCreateSigningBasket(idIn: $idIn) {
            payments {
                _id
                signingBasketId
                authorizationId
                creditorAgent
                client {
                    legalNumber
                }
            }
            redirectLink
        }
    }
`;

const formatPaymentAmount = (amount: string | number | null | undefined): string => {
    if (amount === null || typeof amount === "undefined" || amount === "") {
        return "";
    }

    const numericAmount = typeof amount === "number" ? amount : parseFloat(String(amount).replace(/\s/g, "").replace(",", "."));
    if (isNaN(numericAmount)) {
        return String(amount);
    }

    return twoDecPriceFormat(numericAmount);
};

function SigningStatusNotice({ status, payments }: { status: string | null; payments: OpPayment[] }): React.ReactElement {
    if (!status) {
        return null;
    }

    if (isUnregisteredPaymentRecipientStatus(status)) {
        const accounts = payments
            .map((payment) => {
                const name = payment.creditorName || "Unknown recipient";
                const iban = payment.creditorAccount?.iban;
                return iban ? `${name} (${iban})` : name;
            })
            .filter((line, index, all) => all.indexOf(line) === index);

        return (
            <div className="mt-4 w-full rounded border-2 border-red-700 bg-red-100 px-4 py-4 text-left text-red-800" role="alert">
                <p className="text-xl font-bold">This account is not a registered payment recipient.</p>
                {accounts.length > 0 ? (
                    <ul className="mt-2 list-disc pl-5 text-base">
                        {accounts.map((account) => (
                            <li key={account}>{account}</li>
                        ))}
                    </ul>
                ) : null}
                <p className="mt-3 text-base font-semibold">Signing status: {status}</p>
                <p className="mt-1 text-base">{scaStatusExplanation(status)}</p>
                <p className="mt-3 text-lg font-bold">Please contact Andreas Lindh.</p>
            </div>
        );
    }

    return (
        <div className="mt-4 w-full rounded border border-slate-300 bg-white px-4 py-3 text-left text-sm text-gray-800">
            <p className="font-semibold">Signing status: {status}</p>
            <p className="mt-1">{scaStatusExplanation(status)}</p>
        </div>
    );
}

function PaymentSignSummaryList({ payments }: { payments: OpPayment[] }): React.ReactElement {
    if (payments.length === 0) {
        return null;
    }

    return (
        <ul className="mt-3 w-full space-y-2 text-left">
            {payments.map((payment) => (
                <li
                    key={payment._id}
                    className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm leading-snug text-gray-700 break-words"
                >
                    <span className="font-medium">
                        {formatPaymentAmount(payment.instructedAmount?.amount)} {payment.instructedAmount?.currency || ""}
                    </span>
                    <span className="text-gray-500"> · </span>
                    <span>{payment.client?.name || "—"}</span>
                    <span className="text-gray-500"> → </span>
                    <span>{payment.creditorName || "—"}</span>
                </li>
            ))}
        </ul>
    );
}

export const PaymentSignPanel: React.FC<PaymentSignPanelProps> = ({ isOpen, onDismiss, paymentIds, refetch: refetch }) => {
    const [{ fetching: fetchingPayments, data: dataPayments, error: errorPayments }, refetchPayments] = useQuery({
        query: OP_PAYMENTS_QUERY,
        variables: {
            filter: { idIn: paymentIds }
        }
    });

    const [
        { data: _dataCreateSigningBasket, fetching: fetchingCreateSigningBasket, error: errorCreateSigningBasket },
        opCreateSigningBasket
    ] = useMutation(OP_CREATE_SIGNING_BASKET);

    const [signingBasketId, setSigningBasketId] = useState(null);
    const [redirectData, setRedirectData] = useState<{ redirectLink: string; signingBasketAuthId: string; legalNumber: string } | null>(
        null
    );
    const [signingBasketStatus, setSigningBasketStatus] = useState(null);
    const [paymentsIdsToSign, setPaymentsIdsToSign] = useState([]);

    const { opItem: _opSigningBasket, setOPItem: setOPSigningBasket } = useContext(OPItemContext);

    const isRelevantStatus = useMemo(
        () => [
            OpPaymentStatusEnum.PDNG,
            OpPaymentStatusEnum.RCVD
            //OpPaymentStatusEnum.PATC,  need counter signature, disabled for now. NOT SUPPORTED BY OPEN PAYMENTS API ATM for swdb -> sign in bank
            //OpPaymentStatusEnum.PART  need counter signature, disabled for now. NOT SUPPORTED BY OPEN PAYMENTS API ATM for swdb -> sign in bank
        ],
        []
    );

    useEffect(() => {
        if (dataPayments && dataPayments.opPayments) {
            const paymentsByClientIdAndBic: Record<string, IPaymentDataRow[]> = dataPayments.opPayments.reduce(
                (acc: Record<string, IPaymentDataRow[]>, payment: IPaymentDataRow) => {
                    if (isRelevantStatus.includes(payment.transactionStatus) && payment.clientId) {
                        if (!acc[payment.clientId + "-" + payment.creditorAgent]) {
                            acc[payment.clientId + "-" + payment.creditorAgent] = [];
                        }
                        acc[payment.clientId + "-" + payment.creditorAgent].push(payment);
                    }

                    return acc;
                },
                {} as Record<string, IPaymentDataRow[]>
            );

            // Signing payments for the first client-bic combination that has relevant payments
            for (const clientIdAndBic in paymentsByClientIdAndBic) {
                const payments = paymentsByClientIdAndBic[clientIdAndBic];
                const paymentsIdsToSign = payments.map((payment: IPaymentDataRow) => payment._id);

                if (paymentsIdsToSign.length > 0) {
                    setPaymentsIdsToSign(paymentsIdsToSign);
                    break;
                }
            }
        }
    }, [dataPayments, isRelevantStatus]);

    const displayedPayments = useMemo(() => {
        if (!dataPayments?.opPayments) {
            return [];
        }

        const ids = paymentsIdsToSign.length > 0 ? paymentsIdsToSign : paymentIds;
        const idSet = new Set(ids);
        return dataPayments.opPayments.filter((payment: OpPayment) => idSet.has(payment._id));
    }, [dataPayments, paymentsIdsToSign, paymentIds]);

    const paymentsToSign = useMemo(() => {
        if (!dataPayments?.opPayments || paymentsIdsToSign.length === 0) {
            return [];
        }

        const idSet = new Set(paymentsIdsToSign);
        return dataPayments.opPayments.filter((payment: OpPayment) => idSet.has(payment._id));
    }, [dataPayments, paymentsIdsToSign]);

    const [
        { fetching: fetchingSigningBasketAuthorization, data: dataSigningBasketAuthorization, error: errorSigningBasketAuthorization },
        refetchAuthorization
    ] = useQuery({
        query: OP_SIGNING_BASKET_AUTHORIZATION_QUERY,
        variables: {
            signingBasketId: signingBasketId ? signingBasketId : ""
        },
        pause: !signingBasketId || signingBasketStatus !== ScaStatus.authenticationStarted || signingBasketStatus !== ScaStatus.started
    });

    useEffect(() => {
        if (
            !isOpen ||
            !signingBasketId ||
            (signingBasketStatus !== ScaStatus.authenticationStarted && signingBasketStatus !== ScaStatus.started)
        ) {
            return;
        }
        if (fetchingSigningBasketAuthorization) {
            return;
        }
        const timeoutId = setTimeout(() => {
            refetchAuthorization({ requestPolicy: "network-only" });
        }, 400);
        return () => clearTimeout(timeoutId);
    }, [isOpen, signingBasketId, signingBasketStatus, fetchingSigningBasketAuthorization, refetchAuthorization]);

    useEffect(() => {
        if (
            dataSigningBasketAuthorization &&
            dataSigningBasketAuthorization.opSigningBasketAuthorization &&
            dataSigningBasketAuthorization.opSigningBasketAuthorization.status &&
            !(
                signingBasketStatus === null &&
                (dataSigningBasketAuthorization.opSigningBasketAuthorization.status === ScaStatus.failed ||
                    dataSigningBasketAuthorization.opSigningBasketAuthorization.status === ScaStatus.finalised)
            )
        ) {
            setSigningBasketStatus(dataSigningBasketAuthorization.opSigningBasketAuthorization.status);
        }

        if (signingBasketStatus && signingBasketStatus === ScaStatus.finalised) {
            refetchPayments();
            refetch(paymentsIdsToSign, "refetch"); // refetching payments shown in grid
            setSigningBasketStatus(null);
            setSigningBasketId(null);
            setRedirectData(null);
        }
    }, [dataSigningBasketAuthorization, paymentsIdsToSign, refetch, refetchPayments, signingBasketStatus]);

    const handleStartSigning = async () => {
        setSigningBasketStatus(null);
        // TODO: When counter signature is possible via api (status: PATC or PART), we need to use the same signing basket id for counter signature as
        // was created for the original payment, NOT create a new signing basket as we do below
        const { data, error } = await opCreateSigningBasket({ idIn: paymentsIdsToSign });

        if (error) {
            console.log("handleStartSigning error: ", error);
        } else {
            console.log("handleStartSigning data: ", data);
        }

        if (data && data.opCreateSigningBasket && data.opCreateSigningBasket.payments?.length > 0) {
            setSigningBasketId(data.opCreateSigningBasket.payments[0].signingBasketId);
            if (data.opCreateSigningBasket.redirectLink && data.opCreateSigningBasket.redirectLink !== "") {
                console.log("handleStartSigning redirectLink: ", data.opCreateSigningBasket.redirectLink);
                setRedirectData({
                    redirectLink: data.opCreateSigningBasket.redirectLink,
                    signingBasketAuthId:
                        data.opCreateSigningBasket.payments?.length > 0 ? data.opCreateSigningBasket.payments[0].authorizationId : null,
                    legalNumber:
                        data.opCreateSigningBasket.payments?.length > 0 ? data.opCreateSigningBasket.payments[0].client.legalNumber : null
                });
            }
        }
    };

    const handleSign = (
        signingBasketId: string,
        redirectData: { redirectLink: string; signingBasketAuthId: string; legalNumber: string }
    ) => {
        if (redirectData && redirectData.redirectLink && redirectData.redirectLink !== "") {
            setOPSigningBasket({
                itemId: signingBasketId,
                itemAuthId: redirectData.signingBasketAuthId,
                legalNumber: redirectData.legalNumber,
                bic: "",
                clientId: ""
            });
            window.open(redirectData.redirectLink);
            return;
        } else {
            setSigningBasketId(signingBasketId);
            refetchAuthorization();
        }
    };

    const handleCancel = () => {
        setSigningBasketStatus(null);
        setSigningBasketId(null);
        setRedirectData(null);
        onDismiss();
    };

    const qrValue = dataSigningBasketAuthorization?.opSigningBasketAuthorization?.qrImage as string | undefined;
    const isQrDataUrl = typeof qrValue === "string" && qrValue.startsWith("data:image");
    const creditorAccountNotRegistered = isUnregisteredPaymentRecipientStatus(signingBasketStatus);

    return (
        <Panel isOpen={isOpen} onDismiss={handleCancel} title={"Sign Payments"} width={480}>
            <div className="flex flex-col items-center p-4 text-center">
                <div className="mt-2">
                    <small className="text-gray-500">Payments with status: {isRelevantStatus.join(", ")} can be signed</small>
                </div>
                {displayedPayments.length > 0 ? (
                    <div className="mt-2 w-full">
                        <small className="text-gray-500">Payments to sign</small>
                        <PaymentSignSummaryList payments={displayedPayments} />
                    </div>
                ) : null}
                {paymentsIdsToSign.length > 0 ? (
                    <div className="mt-4">
                        <button
                            className="btn btn-primary mr-2"
                            onClick={handleStartSigning}
                            disabled={fetchingCreateSigningBasket || fetchingPayments || signingBasketStatus === ScaStatus.failed}
                        >
                            Start signing process
                        </button>
                    </div>
                ) : (
                    <div className="mt-2">
                        <span className="text-gray-500 text-lg">No payments possible to sign.</span>
                    </div>
                )}
                {errorPayments ? (
                    <pre
                        className="mt-3 p-2 rounded bg-gray-100 text-gray-900 w-full"
                        style={{ overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                        errorPayments: {JSON.stringify(errorPayments, null, 2)}
                    </pre>
                ) : null}
                {errorCreateSigningBasket ? (
                    <pre
                        className="mt-3 p-2 rounded bg-gray-100 text-gray-900 w-full"
                        style={{ overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                        errorCreateSigningBaskets: {JSON.stringify(errorCreateSigningBasket, null, 2)}
                    </pre>
                ) : null}
                {signingBasketId ? (
                    <div className="flex flex-col items-center text-center">
                        {paymentsToSign.length > 0 ? (
                            <div className="mt-4 w-full">
                                <small className="text-gray-500">Signing</small>
                                <PaymentSignSummaryList payments={paymentsToSign} />
                            </div>
                        ) : null}
                        <div className="mt-4">
                            <small className="text-gray-500">Signing Basket ID: {signingBasketId}</small>
                        </div>
                        <div className="mt-2">
                            {errorSigningBasketAuthorization ? (
                                <pre
                                    className="mt-3 p-2 rounded bg-gray-100 text-gray-900 w-full"
                                    style={{ overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                >
                                    errorSigningBasketAuthorization: {JSON.stringify(errorSigningBasketAuthorization, null, 2)}
                                </pre>
                            ) : null}
                        </div>

                        <SigningStatusNotice status={signingBasketStatus} payments={paymentsToSign} />
                        {redirectData && redirectData.redirectLink && redirectData.redirectLink !== "" && !creditorAccountNotRegistered ? (
                            <div className="mt-3">
                                <small className="text-gray-500">
                                    FYI: This transaction(s) needs to be signed on bank's page by clicking below button. If you have
                                    selected more transactions to sign after, these will need to be initiated in signing process again and
                                    signed separately.
                                </small>
                            </div>
                        ) : null}
                        {creditorAccountNotRegistered ? null : (
                            <div className="mt-4">
                                <button
                                    className="btn btn-primary mr-2"
                                    onClick={() => handleSign(signingBasketId, redirectData)}
                                    disabled={
                                        signingBasketStatus &&
                                        (signingBasketStatus === ScaStatus.authenticationStarted ||
                                            signingBasketStatus === ScaStatus.failed)
                                    }
                                >
                                    {redirectData && redirectData.redirectLink && redirectData.redirectLink !== ""
                                        ? "Go to bank to sign"
                                        : "Get QR code to sign"}
                                </button>
                            </div>
                        )}
                        {qrValue &&
                        !creditorAccountNotRegistered &&
                        (!redirectData || !redirectData.redirectLink || redirectData.redirectLink === "") ? (
                            <div className="mt-3">
                                <h5 className="mb-3">
                                    {signingBasketStatus === null || signingBasketStatus === ScaStatus.authenticationStarted
                                        ? "Scan QR code to log in with BankID. This does not sign the payment."
                                        : "Scan QR code to sign the payment."}
                                </h5>
                                {isQrDataUrl ? (
                                    <img src={qrValue} width={256} height={256} alt="QR code" />
                                ) : (
                                    <QRCodeCanvas value={qrValue} size={256} />
                                )}
                                <p className="mt-3 text-gray-500">Scan this QR code with your signing device</p>
                            </div>
                        ) : (!redirectData || !redirectData.redirectLink || redirectData.redirectLink === "") &&
                          fetchingSigningBasketAuthorization &&
                          !dataSigningBasketAuthorization?.opSigningBasketAuthorization ? (
                            <small className="text-gray-500">Generating QR…</small>
                        ) : null}
                    </div>
                ) : null}
                <button className="btn btn-secondary mt-4" onClick={handleCancel}>
                    Cancel
                </button>
            </div>
        </Panel>
    );
};
